import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as ProductsIcon,
  Print as PrintIcon,
  Group as UsersIcon,
  Business as OrgIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../auth';
import { ORGANIZATION_LIST_USERS_PERMISSION, ORGANIZATION_EDIT_PERMISSION } from '../constants/permissions';
import { useTranslation } from 'react-i18next';
import logoIcon from '../assets/validlabels_1.0.png';

const DRAWER_WIDTH = 236;

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  requiredPermission?: string;
}

const NavListItem: React.FC<{ item: NavItem; location: { pathname: string }; onNavigate: (path: string) => void }> = ({ item, location, onNavigate }) => {
  const isActive =
    location.pathname === item.path ||
    (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        onClick={() => onNavigate(item.path)}
        sx={{
          borderRadius: 1,
          minHeight: 40,
          px: 1.25,
          py: 0.75,
          backgroundColor: isActive ? 'rgba(63, 156, 70, 0.12)' : 'transparent',
          color: isActive ? 'primary.dark' : 'text.primary',
          '&:hover': {
            backgroundColor: isActive ? 'rgba(63, 156, 70, 0.16)' : 'action.hover',
          },
        }}
      >
        <ListItemIcon sx={{ color: isActive ? 'primary.dark' : 'text.secondary', minWidth: 30 }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          sx={{
            '& .MuiListItemText-primary': {
              fontWeight: isActive ? 700 : 500,
              fontSize: '0.84rem',
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions } = useCurrentUser();

  const mainItems: NavItem[] = [
    { text: t('nav.dashboard'), icon: <DashboardIcon fontSize="small" />, path: '/dashboard' },
    { text: t('nav.products'), icon: <ProductsIcon fontSize="small" />, path: '/products' },
    { text: t('nav.printing'), icon: <PrintIcon fontSize="small" />, path: '/printing' },
  ];

  const managementItems: NavItem[] = [
    {
      text: t('nav.users'),
      icon: <UsersIcon fontSize="small" />,
      path: '/users',
      requiredPermission: ORGANIZATION_LIST_USERS_PERMISSION,
    },
    {
      text: t('nav.organisation'),
      icon: <OrgIcon fontSize="small" />,
      path: '/organization/setup',
      requiredPermission: ORGANIZATION_EDIT_PERMISSION,
    },
  ];

  const visibleMainItems = mainItems.filter(
    (item) => !item.requiredPermission || permissions.includes(item.requiredPermission)
  );
  const visibleManagementItems = managementItems.filter(
    (item) => !item.requiredPermission || permissions.includes(item.requiredPermission)
  );

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onToggle();
    }
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      onClose={onToggle}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          borderRadius: 0,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            px: 2,
            py: 0,
            height: 56,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box component="img" src={logoIcon} alt="Valid Labels" sx={{ height: 28, width: 'auto' }} />
        </Box>

        <List sx={{ px: 1.25, py: 1.5, flexGrow: 1 }}>
          {visibleMainItems.map((item) => <NavListItem key={item.path} item={item} location={location} onNavigate={handleNavigation} />)}

          {visibleManagementItems.length > 0 && (
            <>
              <Divider sx={{ mx: 1.5, my: 1 }} />
              {visibleManagementItems.map((item) => <NavListItem key={item.path} item={item} location={location} onNavigate={handleNavigation} />)}
            </>
          )}
        </List>

        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            Valid Labels v1.0
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
